
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Button, Typography, Box,
} from "@mui/material";
import styled, { keyframes } from "styled-components";
import Pagination from "../../utils/Pagination.js";
import CandidateData from "./CandidateData.js";
import JDDetails from "./JDDetails.js";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { ThemeProvider } from '@mui/material/styles';
import recruiterTheme, { FONT_BODY, FONT_DISPLAY, COLORS, TYPE, RADIUS, SHADOW, ELLIPSIS_CSS, PAGE_PADDING, CONTROL_HEIGHT, humanizeStatus } from '../../styles/recruiterTheme';

// MUI icons
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CircularProgress from '@mui/material/CircularProgress';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import TuneIcon from '@mui/icons-material/Tune';
// ─── Animations ───────────────────────────────────────────────────────────────
const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const dropIn = keyframes`
  from { opacity: 0; transform: translateY(-6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ─── Page Wrapper ─────────────────────────────────────────────────────────────
// Inside a dashboard tab the dashboard already supplies the 32px canvas padding,
// so the wrapper only pads itself on the standalone /PendingData route. CandidateData
// gets onBack and adds no padding of its own; JDDetails still pads itself, so the
// wrapper steps back while it is open.
const PageWrapper = styled.div`
  font-family: ${FONT_BODY};
  color: ${COLORS.ink};
  width: 100%;
  box-sizing: border-box;
  ${p => p.$embedded ? '' : `
    background: ${COLORS.background};
    min-height: 100vh;
    padding: ${PAGE_PADDING}px;
  `}
`;

// ─── Header ───────────────────────────────────────────────────────────────────
const HeaderArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 24px;
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
`;

const PageTitle = styled(Typography)`
  && {
    font-family: ${FONT_DISPLAY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.page}px !important;
    line-height: 1.25 !important;
    letter-spacing: -0.02em;
    background: linear-gradient(135deg, #2D1B5E 0%, #6750A4 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const PageSubtitle = styled(Typography)`
  && {
    font-family: ${FONT_BODY} !important;
    font-size: ${TYPE.body}px !important;
    line-height: 1.5 !important;
    color: #8B7DB5 !important;
    font-weight: 500 !important;
    margin-top: 4px !important;
  }
`;

// ─── Stat Cards ───────────────────────────────────────────────────────────────
const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
  animation: ${fadeSlideIn} 0.4s ease both;
`;

const StatCard = styled.div`
  background: ${p => p.gradient || 'linear-gradient(135deg,#6750A4,#4F378B)'};
  border-radius: ${RADIUS.md}px;
  padding: 16px 24px;
  padding-right: 56px;
  min-height: 88px;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  color: #fff;
  position: relative;
  overflow: hidden;
  box-shadow: 0 5px 18px ${p => p.shadow || 'rgba(103,80,164,0.26)'};
  transition: transform 0.2s, box-shadow 0.2s;
  cursor: default;
  &:hover { transform: translateY(-2px); box-shadow: 0 10px 24px ${p => p.shadow || 'rgba(103,80,164,0.36)'}; }
  &::after {
    content: '';
    position: absolute;
    top: -24px; right: -24px;
    width: 72px; height: 72px;
    border-radius: 50%;
    background: rgba(255,255,255,0.1);
  }
`;

const StatNum = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.stat}px;
  font-weight: 800;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  margin-bottom: 4px;
`;

const StatLbl = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.overline}px;
  font-weight: 700;
  letter-spacing: 0.06em;
  line-height: 1.25;
  opacity: 0.88;
  text-transform: uppercase;
`;

const StatIconWrap = styled.div`
  position: absolute;
  top: 50%; right: 16px;
  transform: translateY(-50%);
  display: flex;
  opacity: 0.2;
  svg { font-size: 32px; }
`;

// ─── Status breakdown cards ───────────────────────────────────────────────────
const BreakdownWrap = styled.div`
  margin-bottom: 24px;
  animation: ${fadeSlideIn} 0.45s ease 0.05s both;
`;

const BreakdownCard = styled.div`
  background: #fff;
  border-radius: ${RADIUS.lg}px;
  padding: 24px;
  box-shadow: ${SHADOW.card};
  border: 1px solid ${COLORS.border};
`;

const BreakdownTitle = styled(Typography)`
  && {
    font-family: ${FONT_BODY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.cardTitle}px !important;
    line-height: 1.25 !important;
    color: #2D1B5E;
    margin-bottom: 16px !important;
  }
`;

const StatusPillRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const StatusPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: ${CONTROL_HEIGHT.sm}px;
  box-sizing: border-box;
  background: ${p => p.bg || '#EDE7F6'};
  border: 1px solid ${p => p.border || '#C4B0E8'};
  border-radius: ${RADIUS.sm}px;
  padding: 0 12px;
  cursor: default;
  transition: transform 0.15s;
  &:hover { transform: translateY(-1px); }
`;

const StatusPillNum = styled.span`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.body}px;
  font-weight: 800;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  color: ${p => p.color || '#4F378B'};
`;

const StatusPillLbl = styled.span`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.caption}px;
  font-weight: 600;
  color: ${p => p.color || '#4F378B'};
  white-space: nowrap;
  line-height: 1;
`;

// ─── Table ────────────────────────────────────────────────────────────────────
const TableWrap = styled.div`
  animation: ${fadeSlideIn} 0.5s ease 0.1s both;
`;

// Section header row: title on the left, filter + search on the right.
const SectionHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const TableCard = styled(Paper)`
  && {
    border-radius: ${RADIUS.lg}px !important;
    overflow: hidden;
    box-shadow: ${SHADOW.card} !important;
    border: 1px solid ${COLORS.border};
  }
`;

const SectionLabel = styled(Typography)`
  && {
    font-family: ${FONT_BODY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.section}px !important;
    line-height: 1.25 !important;
    color: #2D1B5E;
    margin: 0 !important;
    display: flex;
    align-items: center;
    gap: 8px;
    &::before {
      content: '';
      width: 4px;
      height: 20px;
      border-radius: 2px;
      background: linear-gradient(180deg, #6750A4, #4F378B);
      flex-shrink: 0;
    }
  }
`;

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 8px;
  border-radius: ${RADIUS.pill}px;
  background: #EDE7F6;
  color: #4F378B;
  font-family: ${FONT_BODY};
  font-size: ${TYPE.caption}px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
`;

const HeadCell = styled(TableCell)`
  && {
    background: linear-gradient(135deg, #4F378B 0%, #6750A4 100%);
    color: #fff !important;
    font-family: ${FONT_BODY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.overline}px !important;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    padding: 12px 16px !important;
    white-space: nowrap;
    vertical-align: middle;
  }
`;

const BodyRow = styled(TableRow)`
  && {
    transition: background 0.15s;
    &:nth-child(even) td { background: #FDFBFF; }
    &:hover td { background: #F3EEF9 !important; }
  }
`;

const BodyCell = styled(TableCell)`
  && {
    font-family: ${FONT_BODY} !important;
    font-size: ${TYPE.small}px !important;
    font-weight: 500 !important;
    color: #3D2B6B !important;
    padding: 12px 16px !important;
    border-bottom: 1px solid #F0EAF9 !important;
    vertical-align: middle;
  }
`;

const LinkBtn = styled(Button)`
  && {
    color: #6750A4 !important;
    font-family: ${FONT_BODY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.small}px !important;
    text-transform: none !important;
    text-decoration: underline !important;
    text-underline-offset: 3px !important;
    min-width: 0;
    min-height: 0;
    padding: 0 !important;
    line-height: 1.5 !important;
    justify-content: flex-start;
    border-radius: 0 !important;
    white-space: nowrap;
    &:hover { background: transparent !important; color: #4F378B !important; }
  }
`;

const MeetLink = styled.a`
  font-family: ${FONT_BODY};
  font-weight: 700;
  font-size: ${TYPE.small}px;
  color: #6750A4;
  text-decoration: underline;
  text-underline-offset: 2px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  &:hover { color: #4F378B; }
`;

// Two-line cell: primary line (14/600 ink) over a muted secondary line.
const CellStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  max-width: ${p => p.$max || 260}px;
`;

const CellPrimary = styled.span`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.body}px;
  font-weight: 600;
  line-height: 1.5;
  color: ${COLORS.ink};
  ${ELLIPSIS_CSS}
`;

const CellSecondary = styled.span`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.caption}px;
  font-weight: 500;
  line-height: 1.5;
  color: ${COLORS.muted};
  ${ELLIPSIS_CSS}
`;

const formatDateTime = (value) => {
  const d = new Date(value);
  if (!value || Number.isNaN(d.getTime())) return value || 'N/A';
  const date = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  return `${date} · ${time}`;
};

// ─── Filter bar ───────────────────────────────────────────────────────────────
const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const SearchInput = styled.input`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.body}px;
  height: ${CONTROL_HEIGHT.lg}px;
  box-sizing: border-box;
  padding: 0 12px;
  border-radius: ${RADIUS.sm}px;
  border: 1.5px solid #D9CFEE;
  outline: none;
  width: 320px;
  max-width: 100%;
  &:focus { border-color: #6750A4; }
  &::placeholder { color: #B0A0D0; }
`;

// ─── Status filter dropdown (replaces the row of chips) ────────────────────────
const FilterDropdownWrap = styled.div`
  position: relative;
`;

const FilterToggleBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  height: ${CONTROL_HEIGHT.lg}px;
  box-sizing: border-box;
  border: 1.5px solid ${p => p.active ? '#6750A4' : '#D9CFEE'};
  background: ${p => p.active ? '#6750A4' : '#fff'};
  color: ${p => p.active ? '#fff' : '#4F378B'};
  font-family: ${FONT_BODY};
  font-weight: 600;
  font-size: ${TYPE.small}px;
  padding: 0 16px;
  border-radius: ${RADIUS.sm}px;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
  &:hover { border-color: #6750A4; }

  svg:first-child { font-size: 16px; }
  .chev { font-size: 20px; transform: rotate(${p => p.open ? '180deg' : '0deg'}); transition: transform 0.15s; }
`;

const FilterCountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  box-sizing: border-box;
  background: ${p => p.active ? '#fff' : '#6750A4'};
  color: ${p => p.active ? '#6750A4' : '#fff'};
  font-size: ${TYPE.overline}px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  border-radius: 999px;
  padding: 0 4px;
`;

const FilterPanel = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 260px;
  max-width: 320px;
  background: #fff;
  border: 1px solid #EDE7F6;
  border-radius: ${RADIUS.md}px;
  box-shadow: 0 10px 28px rgba(45,27,94,0.16);
  z-index: 30;
  padding: 8px;
  animation: ${dropIn} 0.15s ease both;
`;

const FilterPanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 8px 8px 8px;
  border-bottom: 1px solid #EDE7F6;
  margin-bottom: 4px;
`;

const FilterPanelTitle = styled.span`
  font-family: ${FONT_BODY};
  font-weight: 700;
  font-size: ${TYPE.caption}px;
  color: #2D1B5E;
`;

const FilterPanelActions = styled.div`
  display: flex;
  gap: 12px;
`;

const FilterPanelActionBtn = styled.button`
  border: none;
  background: transparent;
  color: #6750A4;
  font-family: ${FONT_BODY};
  font-weight: 700;
  font-size: ${TYPE.caption}px;
  cursor: pointer;
  padding: 0;
  &:hover { text-decoration: underline; }
`;

const FilterOptionList = styled.div`
  max-height: 260px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const FilterOption = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: ${RADIUS.sm}px;
  cursor: pointer;
  font-family: ${FONT_BODY};
  font-size: ${TYPE.small}px;
  font-weight: 500;
  color: #3D2B6B;
  &:hover { background: #F3EEF9; }

  input {
    accent-color: #6750A4;
    width: 16px;
    height: 16px;
    margin: 0;
    flex-shrink: 0;
  }
`;

// ─── Status chip ──────────────────────────────────────────────────────────────
const rejectedStatuses = [
  'ScreeningTest_Failed', 'Recruiter_Rejected', 'ReScreeningTest_Failed',
  'HiringManager_Rejected', 'Rejected', 'Candidate_Declined', 'Candidate_Quit',
];
const passedStatuses = ['Selected', 'ScreeningTest_Passed', 'ReScreeningTest_Passed', 'Round2_Suggested', 'Recruiter_Accepted', 'RecruiterAccepted'];
const interviewStatuses = ['Interview_Scheduled', 'Round2_Scheduled', 'InterviewScheduled'];

// ─── Filter config ─────────────────────────────────────────────────────────
const FILTERABLE_STATUSES_BY_ROLE = {
  recruiter: [
    "PendingCandidateUpload",
    "PendingSeniorReview",
    "NewCandidate",
    "Round2_Suggested",
    "OnHold_TestPassed",
    "ScreeningTest_Passed",
    "ReScreeningTest_Passed",
    "HiringManager_Accepted",
  ],
  hiringManager: [
    "Interviewer_Selected",
    "Recruiter_Accepted",
  ],
  interviewer: [
    "Round2_Scheduled",
    "Interview_Scheduled",
  ],
};

const getFilterableStatuses = (userType) =>
  FILTERABLE_STATUSES_BY_ROLE[userType] || [];

// Lower index = higher priority = shown first
const STATUS_PRIORITY = {
  PendingCandidateUpload:   0,
  PendingSeniorReview:      1,
  NewCandidate:             2,
  Round2_Suggested:         3,
  OnHold_TestPassed:        4,
  ScreeningTest_Passed:     5,
  ReScreeningTest_Passed:   6,
  HiringManager_Accepted:   7,
  Interviewer_Selected:     0,  
  Recruiter_Accepted:       1,
  Round2_Scheduled:         0,
  Interview_Scheduled:      1,
};

const getStatusStyle = (status) => {
  if (!status || status === 'N/A') return { bg: '#EDE7F6', fg: '#4F378B', dot: '#7C5CBF' };
  if (status === 'NewCandidate')              return { bg: '#FFF8E1', fg: '#E65100', dot: '#FFB300' };
  if (status === 'PendingCandidateUpload')    return { bg: '#F0F9FF', fg: '#0369A1', dot: '#38BDF8' };
  if (status === 'PendingSeniorReview')       return { bg: '#FFF3E0', fg: '#EF6C00', dot: '#FB8C00' };
  if (status === 'TailoredResume_Rejected')   return { bg: '#FDECEA', fg: '#B3261E', dot: '#E53935' };
  if (rejectedStatuses.includes(status))      return { bg: '#FDECEA', fg: '#B3261E', dot: '#E53935' };
  if (passedStatuses.includes(status))        return { bg: '#E6F4EA', fg: '#2D7D46', dot: '#2D7D46' };
  if (interviewStatuses.includes(status))     return { bg: '#E3F2FD', fg: '#1565C0', dot: '#1976D2' };
  return { bg: '#EDE7F6', fg: '#4F378B', dot: '#7C5CBF' };
};

const StatusChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 24px;
  padding: 0 12px;
  border-radius: ${RADIUS.pill}px;
  font-family: ${FONT_BODY};
  font-weight: 700;
  font-size: ${TYPE.overline}px;
  white-space: nowrap;
  max-width: 100%;
  background: ${p => p.bg};
  color: ${p => p.fg};
`;

const StatusDot = styled.span`
  width: 6px; height: 6px;
  border-radius: 50%;
  background: ${p => p.color};
  display: inline-block;
  flex-shrink: 0;
`;

// ─── Empty state ──────────────────────────────────────────────────────────────
const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  text-align: center;
  padding: 40px 24px;
  background: #fff;
  border-radius: ${RADIUS.lg}px;
  border: 1px solid ${COLORS.border};
  box-shadow: ${SHADOW.card};
`;

const emptyTitleSx = { fontFamily: FONT_BODY, fontSize: TYPE.cardTitle, fontWeight: 700, lineHeight: 1.25 };
const emptyTextSx = { fontFamily: FONT_BODY, fontSize: TYPE.body, lineHeight: 1.5, color: '#8B7DB5' };

// ─── Pill colours for status breakdown ───────────────────────────────────────
const getStatusPillStyle = (status) => {
  const chip = getStatusStyle(status);
  return { bg: chip.bg + 'CC', border: chip.dot + '55', color: chip.fg };
};

const readStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem('rbotUser')) || {};
  } catch {
    return {};
  }
};

// ─────────────────────────────────────────────────────────────────────────────
const PendingDataContent = ({ email: emailProp, userType: userTypeProp, onBack }) => {
  // Dashboards always pass onBack; the bare /PendingData route does not.
  const embedded = Boolean(onBack);
  // Direct /PendingData route renders without props — fall back to the logged-in user.
  const storedUser = (!emailProp || !userTypeProp) ? readStoredUser() : {};
  const email = emailProp || storedUser.email;
  const userType = userTypeProp || storedUser.userType;
  const [candidates, setCandidates] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [showCandidateData, setShowCandidateData] = useState(false);
  const [selectedJD, setSelectedJD] = useState(null);
  const [isViewJdDetailsOpen, setIsViewJdDetailsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("RecruiterDashboard");
  const [activeStatuses, setActiveStatuses] = useState(getFilterableStatuses(userType));
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const navigate = useNavigate();
  const filterRef = useRef(null);

  const fetchCandidates = async (email, userType) => {
    try {
      setLoading(true);
      setError('');

      const response = await axios.post(
        process.env.REACT_APP_GET_CANDIDATE_API,
        { email, userType },
        { withCredentials: true }
      );

      setCandidates(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching candidates:", error);
      setError(error.response?.data?.message || "Failed to load pending candidates. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    if (email && userType) fetchCandidates(email, userType);
  };

  const handlePageBack = () => {
    if (onBack) onBack();
    else navigate(-1);
  };

  const calculateStatusCounts = (candidatesList) => {
    const counts = {};
    candidatesList.forEach((candidate) => {
      const status = candidate.overallStatus || "N/A";
      counts[status] = (counts[status] || 0) + 1;
    });
    return counts;
  };

  useEffect(() => {
    if (email && userType) {
      fetchCandidates(email, userType);
    } else {
      setLoading(false);
      setError("Unable to identify the logged-in user. Please log in again.");
    }
  }, [email, userType]);

  useEffect(() => { setStatusCounts(calculateStatusCounts(candidates)); }, [candidates]);

  // Close the status filter dropdown when clicking outside of it
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── Filter (by selected statuses + search term) then sort ─────────────────
  const filteredCandidates = candidates
  .filter(c => {
    const roleStatuses = getFilterableStatuses(userType);
    return roleStatuses.length === 0 || activeStatuses.includes(c.overallStatus);
  })
  .filter(c => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    return (
      (c.candidatename || "").toLowerCase().includes(q) ||
      (c.candidateEmail || "").toLowerCase().includes(q) ||
      (c.candidateID || "").toLowerCase().includes(q) ||
      (c.jddetails_id?.jdID || "").toLowerCase().includes(q)
    );
  })
  .sort((a, b) => {
    const pa = STATUS_PRIORITY[a.overallStatus] ?? 99;
    const pb = STATUS_PRIORITY[b.overallStatus] ?? 99;
    if (pa !== pb) return pa - pb;
    const ta = new Date(a.updated_at || a.created_at || 0).getTime();
    const tb = new Date(b.updated_at || b.created_at || 0).getTime();
    return tb - ta;
  });

  const itemsPerPage = 10;
  const totalPages = Math.ceil(filteredCandidates.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedCandidates = filteredCandidates.slice(startIndex, startIndex + itemsPerPage);

  const handlePrevPage = () => setCurrentPage((p) => Math.max(p - 1, 1));
  const handleNextPage = () => setCurrentPage((p) => Math.min(p + 1, totalPages));

  const handleJdIdClick = (selectedJD) => {
    if (!selectedJD) return;
    setIsViewJdDetailsOpen(true);
    setSelectedJD(selectedJD);
    setShowCandidateData(false);
  };

  const handleButtonClick = (candidate) => {
    if (!candidate._id) { console.error("Candidate ID is undefined"); return; }
    setSelectedCandidate(candidate);
    setShowCandidateData(true);
  };

  const handleClose = () => {
    setSelectedJD(null);
    setIsViewJdDetailsOpen(false);
    setActiveTab('pendingData');
  };
  const toggleStatus = (status) => {
  setCurrentPage(1);
  setActiveStatuses(prev =>
    prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
  );
};

  const handleSelectAllStatuses = () => {
    setCurrentPage(1);
    setActiveStatuses(getFilterableStatuses(userType));
  };

  const handleClearAllStatuses = () => {
    setCurrentPage(1);
    setActiveStatuses([]);
  };

  useEffect(() => {
  setActiveStatuses(getFilterableStatuses(userType));
  setCurrentPage(1);
}, [userType]);

  // Derived stats (based on full candidate list, not filtered view)
  const totalCount    = candidates.length;
  const newCount      = candidates.filter(c => c.overallStatus === 'NewCandidate').length;
  const passedCount   = candidates.filter(c => passedStatuses.includes(c.overallStatus)).length;
  const interviewCount = candidates.filter(c => interviewStatuses.includes(c.overallStatus)).length;
  const pendingReviewCount = candidates.filter(c =>
    c.overallStatus === 'PendingSeniorReview' || c.overallStatus === 'PendingCandidateUpload'
  ).length;

  const roleStatuses = getFilterableStatuses(userType);
  const allSelected = roleStatuses.length > 0 && activeStatuses.length === roleStatuses.length;

  // ─── RENDER ──────────────────────────────────────────────────────────────────
  return (
    <PageWrapper $embedded={embedded || isViewJdDetailsOpen}>
      {!showCandidateData && !isViewJdDetailsOpen && (
        <>
          {/* Header */}
          <HeaderArea>
            <HeaderLeft>
              <Button onClick={handlePageBack} size="small" startIcon={<ArrowBackIcon sx={{ fontSize: '16px !important' }} />}
                sx={{ fontFamily: FONT_BODY, fontSize: TYPE.small, textTransform: 'none', color: '#6750A4', fontWeight: 600, height: CONTROL_HEIGHT.sm, px: 1, ml: -1, mb: 1, minWidth: 0, borderRadius: `${RADIUS.sm}px`, '& .MuiButton-startIcon': { mr: 0.5 } }}>
                Back
              </Button>
              <Box sx={{ minWidth: 0 }}>
                <PageTitle>Pending Actions</PageTitle>
                <PageSubtitle>
                  Candidates requiring your attention across all job descriptions
                </PageSubtitle>
              </Box>
            </HeaderLeft>
          </HeaderArea>

          {/* ── Stat Cards ── */}
          <StatsRow>
            <StatCard gradient="linear-gradient(135deg,#6750A4,#4F378B)" shadow="rgba(103,80,164,0.28)">
              <StatNum>{totalCount}</StatNum>
              <StatLbl>Total Pending</StatLbl>
              <StatIconWrap><PeopleAltIcon /></StatIconWrap>
            </StatCard>

            <StatCard gradient="linear-gradient(135deg,#F57F17,#E65100)" shadow="rgba(245,127,23,0.24)">
              <StatNum>{newCount}</StatNum>
              <StatLbl>New Candidates</StatLbl>
              <StatIconWrap><FiberNewIcon /></StatIconWrap>
            </StatCard>

            {/* <StatCard gradient="linear-gradient(135deg,#2E7D32,#1B5E20)" shadow="rgba(46,125,50,0.24)">
              <StatNum>{passedCount}</StatNum>
              <StatLbl>Accepted / Passed</StatLbl>
              <StatIconWrap><CheckCircleOutlineIcon /></StatIconWrap>
            </StatCard> */}

            <StatCard gradient="linear-gradient(135deg,#1565C0,#0D47A1)" shadow="rgba(21,101,192,0.24)">
              <StatNum>{interviewCount}</StatNum>
              <StatLbl>Interviews Scheduled</StatLbl>
              <StatIconWrap><ScheduleIcon /></StatIconWrap>
            </StatCard>

             {/* <StatCard gradient="linear-gradient(135deg,#EF6C00,#E65100)" shadow="rgba(239,108,0,0.24)">
              <StatNum>{pendingReviewCount}</StatNum>
              <StatLbl>Pending Senior Review</StatLbl>
              <StatIconWrap><HourglassTopIcon /></StatIconWrap>
            </StatCard> */}

            {/* <StatCard gradient="linear-gradient(135deg,#E91E8C,#C2185B)" shadow="rgba(233,30,140,0.22)">
              <StatNum>{Object.keys(statusCounts).length}</StatNum>
              <StatLbl>Unique Statuses</StatLbl>
              <StatIconWrap><TrendingUpIcon /></StatIconWrap>
            </StatCard> */}
          </StatsRow>

          {/* ── Status Breakdown ── */}
          {["recruiter", "hiringManager", "interviewer"].includes(userType) &&
            Object.keys(statusCounts).length > 0 && (
            <BreakdownWrap>
              <BreakdownCard>
                <BreakdownTitle>Status Breakdown</BreakdownTitle>
                <StatusPillRow>
                  {Object.keys(statusCounts).map((status) => {
                    const style = getStatusPillStyle(status);
                    return (
                      <StatusPill key={status} bg={style.bg} border={style.border}>
                        <StatusPillNum color={style.color}>{statusCounts[status]}</StatusPillNum>
                        <StatusPillLbl color={style.color}>{humanizeStatus(status)}</StatusPillLbl>
                      </StatusPill>
                    );
                  })}
                </StatusPillRow>
              </BreakdownCard>
            </BreakdownWrap>
          )}

          {/* ── Table ── */}
          <TableWrap>
            {/* ── Section header: title left, status filter + search right ── */}
            <SectionHeaderRow>
            <SectionLabel>
              <HourglassTopIcon sx={{ fontSize: 20, color: '#6750A4' }} />
              Pending Candidates
              {filteredCandidates.length > 0 && (
                <CountBadge>
                  {filteredCandidates.length}
                </CountBadge>
              )}
            </SectionLabel>
            {/* ── Filter dropdown (select/clear) + search ── */}
            <FilterBar>
  {roleStatuses.length > 0 && (
    <FilterDropdownWrap ref={filterRef}>
      <FilterToggleBtn
        type="button"
        active={!allSelected}
        open={filterOpen}
        onClick={() => setFilterOpen(o => !o)}
      >
        <TuneIcon />
        Status filter
        {!allSelected && (
          <FilterCountBadge active={!allSelected}>{activeStatuses.length}</FilterCountBadge>
        )}
        <KeyboardArrowDownIcon className="chev" />
      </FilterToggleBtn>

      {filterOpen && (
        <FilterPanel>
          <FilterPanelHeader>
            <FilterPanelTitle>Filter by status</FilterPanelTitle>
            <FilterPanelActions>
              <FilterPanelActionBtn type="button" onClick={handleSelectAllStatuses}>
                Select all
              </FilterPanelActionBtn>
              <FilterPanelActionBtn type="button" onClick={handleClearAllStatuses}>
                Clear
              </FilterPanelActionBtn>
            </FilterPanelActions>
          </FilterPanelHeader>
          <FilterOptionList>
            {roleStatuses.map(status => (
              <FilterOption key={status}>
                <input
                  type="checkbox"
                  checked={activeStatuses.includes(status)}
                  onChange={() => toggleStatus(status)}
                />
                {humanizeStatus(status)}
              </FilterOption>
            ))}
          </FilterOptionList>
        </FilterPanel>
      )}
    </FilterDropdownWrap>
  )}

  <SearchInput
    placeholder="Search name, email, candidate ID, JD ID..."
    value={searchTerm}
    onChange={(e) => { setCurrentPage(1); setSearchTerm(e.target.value); }}
  />
</FilterBar>
            </SectionHeaderRow>


  {loading && (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.75)',
        backdropFilter: 'blur(3px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <CircularProgress
          size={45}
          thickness={4}
          sx={{
            color: '#6750A4',
          }}
        />

        <Typography
          sx={{
            fontFamily: FONT_BODY,
            fontSize: TYPE.body,
            fontWeight: 600,
            color: '#4F378B',
          }}
        >
          Loading candidates...
        </Typography>
      </Box>
    </Box>
  )}

  {/* Your existing content */}
            {loading && candidates.length === 0 ? null : error ? (
              <EmptyState>
                <Typography sx={{ ...emptyTitleSx, color: '#B3261E' }}>
                  Couldn't load pending candidates
                </Typography>
                <Typography sx={emptyTextSx}>
                  {error}
                </Typography>
                <Button onClick={handleRetry}
                  sx={{ fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none', color: '#6750A4', border: '1.5px solid #C4B0E8', borderRadius: `${RADIUS.sm}px`, height: 36, px: 2 }}>
                  Retry
                </Button>
              </EmptyState>
            ) : paginatedCandidates.length > 0 ? (
              <TableCard>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <HeadCell>JD / Job Role</HeadCell>
                        <HeadCell>Candidate ID</HeadCell>
                        <HeadCell>Candidate</HeadCell>
                        {userType === "interviewer" && (
                          <HeadCell>Scheduled Time</HeadCell>
                        )}
                        <HeadCell align="center">Status</HeadCell>
                        {userType === "interviewer" && (
                          <HeadCell align="right">Meeting Link</HeadCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedCandidates.map((candidate) => {
                        const chip = getStatusStyle(candidate.overallStatus);
                        const lastInterview = candidate.interviewid?.length > 0
                          ? candidate.interviewid[candidate.interviewid.length - 1]
                          : null;
                        return (
                          <BodyRow key={candidate._id}>
                            <BodyCell>
                              <CellStack $max={240}>
                                {candidate.jddetails_id ? (
                                  <LinkBtn onClick={() => handleJdIdClick(candidate.jddetails_id)} sx={{ alignSelf: 'flex-start' }}>
                                    {candidate.jddetails_id.jdID || "N/A"}
                                  </LinkBtn>
                                ) : (
                                  <span style={{ color: COLORS.faint, lineHeight: 1.5 }}>N/A</span>
                                )}
                                <CellSecondary title={candidate.jddetails_id?.jobRole || ''}>
                                  {candidate.jddetails_id?.jobRole || "N/A"}
                                </CellSecondary>
                              </CellStack>
                            </BodyCell>
                            <BodyCell sx={{ whiteSpace: 'nowrap' }}>
                              <LinkBtn onClick={() => handleButtonClick(candidate)}>
                                {candidate.candidateID}
                              </LinkBtn>
                            </BodyCell>
                            <BodyCell>
                              <CellStack $max={280}>
                                <CellPrimary title={candidate.candidatename || ''}>
                                  {candidate.candidatename || "N/A"}
                                </CellPrimary>
                                <CellSecondary title={candidate.candidateEmail || ''}>
                                  {candidate.candidateEmail || "N/A"}
                                </CellSecondary>
                                {candidate.phone && (
                                  <CellSecondary style={{ fontSize: TYPE.overline, fontVariantNumeric: 'tabular-nums' }}>
                                    {candidate.phone}
                                  </CellSecondary>
                                )}
                              </CellStack>
                            </BodyCell>

                            {userType === "interviewer" && (
                              <BodyCell sx={{ color: `${COLORS.muted} !important`, fontSize: '12px !important', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                                {lastInterview ? formatDateTime(lastInterview.dateAndTime) : "N/A"}
                              </BodyCell>
                            )}

                            <BodyCell align="center">
                              <StatusChip bg={chip.bg} fg={chip.fg} title={candidate.overallStatus || ''}>
                                <StatusDot color={chip.dot} />
                                {candidate.overallStatus ? humanizeStatus(candidate.overallStatus) : "N/A"}
                              </StatusChip>
                            </BodyCell>

                            {userType === "interviewer" && (
                              <BodyCell align="right">
                                {lastInterview?.meet_link ? (
                                  <MeetLink href={lastInterview.meet_link} target="_blank" rel="noopener noreferrer">
                                    <VideoCallIcon sx={{ fontSize: 16 }} />
                                    Join
                                  </MeetLink>
                                ) : "N/A"}
                              </BodyCell>
                            )}
                          </BodyRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                {totalPages > 1 && (
                  <Pagination
                    itemsPerPage={itemsPerPage} currentPage={currentPage}
                    totalPages={totalPages} handlePrevPage={handlePrevPage} handleNextPage={handleNextPage}
                  />
                )}
              </TableCard>
            ) : (
              <EmptyState>
                <Typography sx={{ ...emptyTitleSx, color: '#C4B0E8' }}>
                  All caught up!
                </Typography>
                <Typography sx={{ ...emptyTextSx, color: '#B0A0D0' }}>
                  No pending candidates found at this time.
                </Typography>
              </EmptyState>
            )}
          </TableWrap>
        </>
      )}

      {showCandidateData && (
        <CandidateData
          candidateId={selectedCandidate._id}
          userRole={userType}
          candidateEmail={selectedCandidate.candidateEmail}
          overallStatus={selectedCandidate.overallStatus}
          jdId={selectedCandidate.jddetails_id?._id}
          jobDescription={selectedCandidate.jddetails_id?.jobDescription}
          salaryRange={selectedCandidate.jddetails_id?.salaryRange}
          companyName={selectedCandidate.jddetails_id?.companyName}
          recruiterEmail={email}
          skills={selectedCandidate.jddetails_id?.skills}
          experience={selectedCandidate.jddetails_id?.experience}
          interviewid={selectedCandidate.interviewid}
          onBack={() => {
            setShowCandidateData(false);
            if (email && userType) fetchCandidates(email, userType);
          }}
        />
      )}

      {isViewJdDetailsOpen && (
        <JDDetails
          jdDetails={selectedJD}
          userRole="recruiter"
          recruiterEmail={email}
          jobDescription={selectedJD?.jobDescription}
          salaryRange={selectedJD?.salaryRange}
          companyName={selectedJD?.companyName}
          readOnly={true}
          onBack={handleClose}
        />
      )}
    </PageWrapper>
  );
};

// Standalone /PendingData route renders outside RecruiterDashboard — bring the theme along.
const PendingData = (props) => (
  <ThemeProvider theme={recruiterTheme}>
    <PendingDataContent {...props} />
  </ThemeProvider>
);

export default PendingData;